'use client';

import { useEffect, useRef, useState } from 'react';

import { useInView, useReducedMotion } from 'motion/react';

import { cn } from '@kit/ui/utils';

/**
 * Shared primitives for the four domain demo animations (Research, Create,
 * Qualify, Deliver). Every animation obeys the selenos-marketing-page skill:
 * motion only via transform/opacity, plays only in view, pauses offscreen, and
 * freezes on a legible end-state under prefers-reduced-motion.
 */

interface DemoLoopOpts {
  /** Per-gap durations (length steps-1). Falls back to stepMs for all. */
  durations?: number[];
  stepMs?: number;
  /** How long the final payoff state holds before the loop fades out. */
  holdMs?: number;
  /** Graceful fade-out before the restart. */
  resetMs?: number;
  /** Start offset, to desync sibling demos. */
  offsetMs?: number;
}

/**
 * Drives a looping step index with real rhythm: variable per-step timing, a
 * hold on the payoff, and a graceful fade-out before restart (instead of a hard
 * cut). Gated to in-view, paused offscreen, frozen on the end-state under
 * reduced motion. `fading` is true during the reset fade so the demo content
 * eases out rather than snapping.
 */
export function useDemoLoop(steps: number, opts: DemoLoopOpts = {}) {
  const { stepMs = 1500, holdMs = 2400, resetMs = 450, offsetMs = 0 } = opts;
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { amount: 0.45 });
  const reduce = useReducedMotion();
  const [step, setStep] = useState(0);
  const [fading, setFading] = useState(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Stable key for the durations array so the effect doesn't re-run each render.
  const durKey = (opts.durations ?? []).join(',');

  useEffect(() => {
    const clearAll = () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
    if (reduce) {
      setStep(steps - 1);
      setFading(false);
      return clearAll;
    }
    if (!inView) return clearAll;

    const gaps = opts.durations ?? Array(Math.max(0, steps - 1)).fill(stepMs);
    const at = (ms: number, fn: () => void) => {
      timers.current.push(setTimeout(fn, ms));
    };

    const run = () => {
      clearAll();
      setStep(0);
      setFading(false);
      let t = offsetMs + 60;
      for (let i = 1; i < steps; i++) {
        t += gaps[i - 1] ?? stepMs;
        const idx = i;
        at(t, () => setStep(idx));
      }
      t += holdMs;
      at(t, () => setFading(true));
      t += resetMs;
      at(t, run);
    };

    run();
    return clearAll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, reduce, steps, stepMs, holdMs, resetMs, offsetMs, durKey]);

  return { ref, step, reduce, fading, inView };
}

/** Honest "example" tag — every product mock carries it (data-honest). */
export function ExampleLabel({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'sel-mono text-[0.625rem] tracking-[0.14em] text-[var(--sel-faint)] uppercase',
        className,
      )}
    >
      example
    </span>
  );
}

/** The light product-mock card every domain animation renders inside. */
export function DemoFrame({
  ref,
  label,
  children,
  className,
  fading,
}: {
  ref?: React.Ref<HTMLDivElement>;
  label: string;
  children: React.ReactNode;
  className?: string;
  /** When true, the demo body eases to 0 opacity (graceful loop reset). */
  fading?: boolean;
}) {
  return (
    <div
      ref={ref}
      className={cn(
        'relative flex h-[320px] w-full flex-col overflow-hidden rounded-2xl border border-[var(--sel-line)] bg-[var(--sel-card)] p-5 shadow-[0_1px_2px_rgb(20_32_31/4%),0_12px_32px_-12px_rgb(20_32_31/12%)]',
        className,
      )}
    >
      <div className="mb-3 flex items-center justify-between">
        <span className="sel-mono text-[0.6875rem] tracking-[0.12em] text-[var(--sel-muted)] uppercase">
          {label}
        </span>
        <ExampleLabel />
      </div>
      <div
        className="relative min-h-0 flex-1"
        style={{
          opacity: fading ? 0 : 1,
          transition: 'opacity 400ms ease',
        }}
      >
        {children}
      </div>
    </div>
  );
}
